import { Dropdown } from '@/components/Dropdown/Dropdown';
import { EditableTitle } from '@/components/Title/EditableTitle';

import { useAnalytics } from '@/services/analytics';
import { CampaignAccess, CampaignStatus, ICampaign } from '@d-id/types';
import styled from '@emotion/styled';
import { Divider, MenuList, MenuItem as MuiMenuItem } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_CAMPAIGN_NAME } from '../../../../constants';

import { RenameMenuItem } from '@/components/Dropdown/MenuItems/RenameMenuItem';
import { ThreeDotsCircle } from '@/components/ThreeDotsCircle';
import { useUpdateCampaign } from '@/pages/campaign/ListPage/api';
import { DeleteCampaignModal } from '@/pages/campaign/ListPage/components/Modals';
import { useFlag } from '@/services/featureFlags';
import { CampaignMenuItem } from './CampaignMenuItem';
import { DeleteMenuItem } from './DeleteMenuItem';
import { modalsProps, ModalType } from './modalsConfig';
import { PublishMenuItem } from './PublishMenuItem';
import { UnPublishMenuItem } from './UnPublishMenuItem';

const StyledList = styled(MenuList)`
    padding: 0;

    &:focus {
        outline: none;
    }
`;

const StyledDivider = styled(Divider)`
    margin: 1rem 0;
`;

const NameContainer = styled(MuiMenuItem)`
    border-radius: 8px;
    padding: 6px;

    &:hover,
    &:focus {
        background-color: #fff;
    }
`;

const CampaignName = styled(EditableTitle)`
    font-weight: 700;
    font-size: 20px;
    letter-spacing: 0.05rem;
    overflow: hidden;

    &:focus,
    &:hover {
        border: none;
    }

    &:not(:focus) {
        text-overflow: ellipsis;
    }
`;

const CreationDate = styled('p')`
    font-size: 12px;
    color: ${({ theme }) => theme.colors.black[600]};
    margin-top: 4px;
    margin-left: 8px;
`;

const OptionsButtonWrapper = styled.div`
    position: absolute;
    right: 16px;
    top: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    z-index: 2;
`;

const dateFormatOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

interface Props {
    campaign: ICampaign;
    onOpen: () => void;
    onClose: () => void;
    openAnalyticsModal: () => void;
    openAssetsModal: () => void;
    openCreditsCalculatorModal: () => void;
}

export const OptionsList = ({ campaign, onOpen, onClose, openAnalyticsModal, openAssetsModal, openCreditsCalculatorModal }: Props) => {
    const [name, setName] = useState(campaign.name);
    const [itemToDelete, setItemToDelete] = useState<ICampaign | null>(null);

    const mixpanel = useAnalytics();
    const { mutateAsync } = useUpdateCampaign(campaign);

    const nameRef = useRef<HTMLDivElement | null>(null);

    const [closed, setClosed] = useState<boolean>(true);
    const creditsToMinutesEnabled = useFlag('credits_to_minutes_enabled', false);

    useEffect(() => {
        if (campaign.name) {
            setName(campaign.name);
        }
    }, [campaign.name]);

    const updateName = async (name: string) => {
        try {
            await mutateAsync({ name: name.trim() || DEFAULT_CAMPAIGN_NAME });
        } catch (e) {
            setName(campaign.name);
        }
    };

    const handleCloseMenu = () => {
        setClosed(true);
        onClose?.();
    };

    const handleOpenMenu = () => {
        setClosed(false);
        onOpen?.();
    };
    const getModalProps = (modal: ModalType) => {
        return {
            campaign,
            closeMenu: handleCloseMenu,
            ...modalsProps[modal],
        };
    };

    const items = useMemo(
        () =>
            [
                {
                    label: <PublishMenuItem campaign={campaign} closeMenu={handleCloseMenu} onPublish={openAssetsModal} />,
                    show: campaign.access === CampaignAccess.PRIVATE,
                },
                {
                    label: <CampaignMenuItem onClick={openAnalyticsModal} {...getModalProps(ModalType.ANALYTICS)} />,
                    show: true,
                },
                {
                    label: <CampaignMenuItem onClick={openAssetsModal} {...getModalProps(ModalType.ASSETS)} />,
                    show: campaign.access === CampaignAccess.PUBLIC,
                },
                {
                    label: (
                        <CampaignMenuItem
                            onClick={() => {
                                const url = campaign.result_url.replace(/{{.*?}}/g, 'preview');
                                window.open(url, '_blank');
                            }}
                            {...getModalProps(ModalType.PREVIEW_LANDING_PAGE)}
                        />
                    ),
                    show: campaign.access === CampaignAccess.PUBLIC,
                },

                {
                    label: (
                        <CampaignMenuItem
                            onClick={openCreditsCalculatorModal}
                            {...getModalProps(creditsToMinutesEnabled ? ModalType.CREDITS_CALCULATOR : ModalType.MINUTES_CALCULATOR)}
                        />
                    ),
                    show: true,
                },
                {
                    label: <RenameMenuItem titleRef={nameRef} />,
                    show: true,
                },
                { label: <StyledDivider />, show: campaign.status !== CampaignStatus.Error },
                {
                    label: <UnPublishMenuItem campaign={campaign} closeMenu={handleCloseMenu} />,
                    show: campaign.access !== CampaignAccess.PRIVATE,
                },
                {
                    label: (
                        <DeleteMenuItem
                            campaign={campaign}
                            closeMenu={handleCloseMenu}
                            onDelete={() => {
                                setItemToDelete(campaign);
                            }}
                        />
                    ),
                    show: true,
                },
            ].filter(item => item.show),
        [campaign, mixpanel, openAnalyticsModal, openAssetsModal, openCreditsCalculatorModal]
    );

    return (
        <>
            <Dropdown
                closed={closed}
                onOpen={handleOpenMenu}
                onClose={handleCloseMenu}
                trigger={
                    <OptionsButtonWrapper id="campaign-option-btn">
                        <ThreeDotsCircle isActive={!closed} />
                    </OptionsButtonWrapper>
                }
                items={items}
                renderMenuWrapper={children => (
                    <StyledList>
                        <NameContainer
                            onKeyDown={e => {
                                e.stopPropagation();
                            }}>
                            <CampaignName
                                ref={nameRef}
                                fontSize={20}
                                value={name}
                                onChange={setName}
                                onBlur={value => {
                                    mixpanel?.track('campaigns-name', {
                                        event: 'change',
                                        newName: value,
                                        from: 'campaign-menu',
                                        campaign,
                                    });
                                    updateName(value);
                                }}
                            />
                        </NameContainer>
                        <CreationDate>Created {new Date(campaign.created_at).toLocaleDateString('en-US', dateFormatOptions)}</CreationDate>
                        <StyledDivider />
                        {children}
                    </StyledList>
                )}
            />
            <DeleteCampaignModal itemToDelete={itemToDelete} onClose={() => setItemToDelete(null)} />
        </>
    );
};
